/**
 * Practitioner Questionnaire Utility (UPDATED)
 * Saves questionnaire answers per-product into ONE customer metafield (JSON stored as text).
 *
 * Metafield:
 * - Owner: Customer
 * - Namespace: custom
 * - Key: practitioner
 * - Type: multi_line_text_field (stores JSON text)
 *
 * REQUIRED: Shopify app endpoint at /apps/practitioner-survey/save must MERGE:
 * - Read current metafield JSON (if empty -> {})
 * - Update entry for storage_key (product:<id> or handle:<handle>)
 * - Write back JSON
 * - Create timeline note
 */

(function () {
  'use strict';

  function safeJsonParse(str, fallback) {
    try {
      if (!str || typeof str !== 'string') return fallback;
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  function getProductContext() {
    var productFormEl = document.querySelector('product-form.product-form');
    var productId = productFormEl ? productFormEl.getAttribute('data-product-id') : '';
    var productHandle = productFormEl ? productFormEl.getAttribute('data-product-handle') : '';

    if (!productHandle) {
      var urlParts = window.location.pathname.split('/');
      productHandle = urlParts[urlParts.length - 1] || '';
    }

    // Prefer productId for stability
    var storageKey = productId ? ('product:' + productId) : (productHandle ? ('handle:' + productHandle) : 'unknown');

    return {
      product_id: productId || '',
      product_handle: productHandle || '',
      product_url: window.location.href,
      storage_key: storageKey
    };
  }

  /**
   * Save questionnaire answers to customer metafield via app endpoint
   * @param {Object} answers - The questionnaire answers
   * @returns {Promise<Object>} resolves surveyData
   */
  function saveQuestionnaireToMetafield(answers) {
    return new Promise(function (resolve, reject) {
      var now = new Date();
      var dateTime = now.toISOString();
      var dateTimeFormatted = now.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      var ctx = getProductContext();

      // Build surveyData (single submission)
      var surveyData = {
        date_time: dateTime,
        date_time_formatted: dateTimeFormatted,
        timestamp: Math.floor(now.getTime() / 1000),
        questionnaire_type: answers && answers.special_condition !== undefined ? 'inline' : 'account_page',
        product_id: answers.product_id || ctx.product_id || '',
        product_handle: answers.product_handle || ctx.product_handle || '',
        product_url: answers.product_url || ctx.product_url || window.location.href,
        storage_key: answers.storage_key || ctx.storage_key || 'unknown',
        answers: {}
      };

      if (surveyData.questionnaire_type === 'inline') {
        surveyData.answers = {
          special_condition: answers.special_condition || '',
          allergies: answers.allergies || '',
          medication: answers.medication || '',
          terms_accepted: !!answers.terms_accepted,
          allergies_detail: answers.allergies_detail || '',
          overview: (function () {
            var ov = answers.overview || '';
            var dn = (answers.document && answers.document.filename) || answers.document_filename || '';
            if (dn && ov.indexOf('DOCUMENT:') === -1) return '📎 DOCUMENT: ' + dn + '\n' + ov;
            return ov;
          })(),
          document_filename: answers.document_filename || '',
          document: answers.document || null
        };
      } else if (answers.for_whom !== undefined) {
        surveyData.answers = {
          for_whom: answers.for_whom || '',
          practitioner_name: answers.practitioner_name || '',
          notes: answers.notes || ''
        };
      } else {
        surveyData.answers = answers || {};
      }

      // LocalStorage backup: store per-product entries too (client-only backup)
      try {
        var existing = safeJsonParse(localStorage.getItem('practitioner_survey_entries'), {});
        if (!existing || typeof existing !== 'object') existing = {};

        var key = surveyData.storage_key || 'unknown';
        if (!existing[key]) {
          existing[key] = { last_submission: null, history: [] };
        }
        if (!existing[key].history || !Array.isArray(existing[key].history)) existing[key].history = [];

        existing[key].last_submission = surveyData;
        existing[key].history.push(surveyData);

        // keep last 20 submissions per product
        if (existing[key].history.length > 20) {
          existing[key].history = existing[key].history.slice(existing[key].history.length - 20);
        }

        localStorage.setItem('practitioner_survey_entries', JSON.stringify(existing));
      } catch (e) {
        console.warn('Could not save to localStorage', e);
      }

      // Timeline note (shows product + answers)
      var timelineNote = 'Practitioner Survey submitted on ' + dateTimeFormatted;
      if (surveyData.product_handle) timelineNote += '\nProduct: ' + surveyData.product_handle;
      if (surveyData.product_id) timelineNote += '\nProduct ID: ' + surveyData.product_id;

      if (surveyData.questionnaire_type === 'inline') {
        timelineNote += '\n\nAnswers:\n';
        timelineNote += '- Special Medical Conditions: ' + (surveyData.answers.special_condition || 'N/A') + '\n';
        timelineNote += '- Allergies: ' + (surveyData.answers.allergies || 'N/A') + '\n';
        timelineNote += '- Medications: ' + (surveyData.answers.medication || 'N/A') + '\n';
        timelineNote += '- Terms Accepted: ' + (surveyData.answers.terms_accepted ? 'Yes' : 'No');
        if (surveyData.answers.allergies_detail) timelineNote += '\n- Allergies Detail: ' + surveyData.answers.allergies_detail;
        if (surveyData.answers.overview) timelineNote += '\n- Overview: ' + surveyData.answers.overview;
        var docName = (surveyData.answers.document && surveyData.answers.document.filename) || surveyData.answers.document_filename;
        if (docName) timelineNote += '\n- Document: ' + docName;
      }

      // Call app endpoint
      var apiEndpoint = '/apps/practitioner-survey/save';

      fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          merge_mode: 'per_product',
          storage_key: surveyData.storage_key,

          metafield_namespace: 'custom',
          metafield_key: 'practitioner',
          metafield_type: 'multi_line_text_field',

          // send the single survey entry; server will merge into JSON map
          survey_entry: surveyData,

          // timeline note
          timeline_note: timelineNote,
          timeline_note_date: dateTime
        })
      })
        .then(function (response) {
          if (!response.ok) {
            console.warn('Could not save to metafield endpoint. Data saved to localStorage only.', response.status);
            return resolve(surveyData);
          }
          return response.json().then(function (data) {
            console.log('Questionnaire saved successfully', data);
            resolve(surveyData);
          });
        })
        .catch(function (error) {
          console.warn('Metafield endpoint not available. Data saved to localStorage only.', error);
          resolve(surveyData);
        });
    });
  }

  /**
   * Collect answers from inline questionnaire form (UPDATED)
   * Includes overview + product_id/product_handle/storage_key
   */
  function collectQuestionnaireAnswers(questionnaireElement) {
    if (!questionnaireElement) return null;

    var specialCondition = questionnaireElement.querySelector('input[name="special_condition"]:checked');
    var allergies = questionnaireElement.querySelector('input[name="allergies"]:checked');
    var medication = questionnaireElement.querySelector('input[name="medication"]:checked');
    var terms = questionnaireElement.querySelector('.practitioner-terms');

    var allergiesDetail = questionnaireElement.querySelector('input[name="allergies_detail"], textarea[name="allergies_detail"]');
    var overviewEl = questionnaireElement.querySelector('textarea[name="practitioner_overview"]');
    var docInput = questionnaireElement.querySelector('#practitioner_document');

    var ctx = getProductContext();

    return {
      special_condition: specialCondition ? specialCondition.value : '',
      allergies: allergies ? allergies.value : '',
      medication: medication ? medication.value : '',
      terms_accepted: terms ? !!terms.checked : false,
      allergies_detail: allergiesDetail ? (allergiesDetail.value || '') : '',
      overview: overviewEl ? (overviewEl.value || '').trim() : '',

      product_id: ctx.product_id,
      product_handle: ctx.product_handle,
      product_url: ctx.product_url,
      storage_key: ctx.storage_key,
      document_filename: docInput && docInput.files.length > 0 ? docInput.files[0].name : ''
    };
  }

  function collectAccountPageAnswers(formElement) {
    if (!formElement) return null;

    var forWhom = formElement.querySelector('#forWhom');
    var practitionerName = formElement.querySelector('#practitionerName');
    var notes = formElement.querySelector('#notes');
    var ctx = getProductContext();

    return {
      for_whom: forWhom ? forWhom.value : '',
      practitioner_name: practitionerName ? practitionerName.value : '',
      notes: notes ? notes.value : '',
      product_id: ctx.product_id,
      product_handle: ctx.product_handle,
      product_url: window.location.href,
      storage_key: ctx.storage_key
    };
  }

  window.PractitionerQuestionnaire = {
    saveToMetafield: saveQuestionnaireToMetafield,
    collectAnswers: collectQuestionnaireAnswers,
    collectAccountPageAnswers: collectAccountPageAnswers
  };
})();
